import { useEffect, useState } from "react";

import { toast } from "react-toastify";

import { TPremiumPackage, TPremiumPlan } from "../@types";
import { apis, callApi } from "../api";
import { useAccount } from "./useAccount";

export type TPremiumPackageService = {
	numLoading: number;
	premiumPackages: TPremiumPackage[];
	currentPlans: TPremiumPlan[];
	listeners: (() => void)[];

	addEventListener: (cb: () => void) => void;
	removeEventListener: (cb: () => void) => void;
	savePremiumPackages: (premiumPackages: TPremiumPackage[]) => void;
	plusNumLoading: () => void;
	minusNumLoading: () => void;
	saveCurrentPlans: (currentPlans: TPremiumPlan[]) => void;
	cancelSubscription: (subscriptionId: string) => void;
	emitChanges: () => void;
	clearData: () => void;
};

export const PremiumPackageService: TPremiumPackageService = {
	numLoading: 0,
	premiumPackages: [],
	currentPlans: [],
	listeners: [],

	addEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
		this.listeners.push(cb);
	},

	plusNumLoading() {
		this.numLoading++;
	},

	minusNumLoading() {
		this.numLoading = Math.max(this.numLoading - 1, 0);
	},

	savePremiumPackages(premiumPackages) {
		this.premiumPackages = premiumPackages;
	},

	saveCurrentPlans(currentPlans) {
		this.currentPlans = currentPlans;
	},

	cancelSubscription(subscriptionId) {
		this.currentPlans = this.currentPlans.map((currentPlan) => {
			if (currentPlan.subscriptionId === subscriptionId) {
				return { ...currentPlan, canceled_at: new Date().toISOString() };
			}
			return currentPlan;
		});
		this.emitChanges();
	},

	emitChanges() {
		for (const listener of this.listeners) {
			listener();
		}
	},

	removeEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
	},

	clearData() {
		this.premiumPackages = [];
		this.currentPlans = [];
		this.emitChanges();
	},
};

export function usePremiumPackageInit() {
	const { account } = useAccount();

	useEffect(() => {
		const getListPackages = async () => {
			try {
				PremiumPackageService.plusNumLoading();
				PremiumPackageService.emitChanges();
				const response = await callApi({
					method: "GET",
					url: apis.GET_PACKAGES,
				});

				if (!response) {
					return;
				}

				PremiumPackageService.savePremiumPackages(response.packages);
			} catch (error) {
			} finally {
				PremiumPackageService.minusNumLoading();
				if (PremiumPackageService.numLoading === 0) {
					PremiumPackageService.emitChanges();
				}
			}
		};

		getListPackages();
	}, []);

	useEffect(() => {
		const getActivePlan = async () => {
			try {
				if (!account) {
					return;
				}

				PremiumPackageService.plusNumLoading();
				PremiumPackageService.emitChanges();

				const response = await callApi({
					method: "GET",
					url: apis.SUBSCRIPTION_PLAN,
				});

				if (!response) {
					return;
				}

				PremiumPackageService.saveCurrentPlans(
					response.subscriptions.filter(
						(plan: TPremiumPlan) => plan.status === "active",
					),
				);
			} catch (error) {
			} finally {
				PremiumPackageService.minusNumLoading();
				if (PremiumPackageService.numLoading === 0) {
					PremiumPackageService.emitChanges();
				}
			}
		};

		getActivePlan();
	}, [account]);
}

export function usePremiumPackage() {
	const [premiumPackages, setPremiumPackages] = useState(
		PremiumPackageService.premiumPackages,
	);
	const [currentPlans, setCurrentPlans] = useState(
		PremiumPackageService.currentPlans,
	);
	const [loading, setLoading] = useState(PremiumPackageService.numLoading > 0);

	useEffect(() => {
		const updateData = () => {
			setPremiumPackages(PremiumPackageService.premiumPackages);
			setCurrentPlans(PremiumPackageService.currentPlans);
			setLoading(PremiumPackageService.numLoading > 0);
		};

		PremiumPackageService.addEventListener(updateData);

		return () => {
			PremiumPackageService.removeEventListener(updateData);
		};
	}, []);

	const cancelSubscription = async (subscriptionId: string) => {
		try {
			PremiumPackageService.plusNumLoading();
			PremiumPackageService.emitChanges();
			const response = await callApi({
				method: "DELETE",
				url: `${apis.SUBSCRIPTION_PLAN}/${subscriptionId}`,
			});

			if (!response) {
				return;
			}

			PremiumPackageService.cancelSubscription(subscriptionId);
			toast.success("Cancel subscription success!");
		} catch (error: any) {
			if (error?.response?.data?.message) {
				toast.error(error.response.data.message);
			}
		} finally {
			PremiumPackageService.minusNumLoading();
			PremiumPackageService.emitChanges();
		}
	};

	return {
		loading,
		premiumPackages,
		currentPlans,
		cancelSubscription,
	};
}
